import { useRef, useState } from "react";
import { ArrowLeftRight, Camera, Loader2, Plus, Star, Trash2 } from "lucide-react";

import { api, photoUri, uploadFile } from "@/lib/api";
import { useToast } from "@/components/toast";
import { Modal } from "@/components/modal";
import { Button } from "@/components/ui";
import { MatchCard } from "@/components/match-card";

/** Browser photo upload to the same object-storage endpoints as the app (POST /profile/photos). */
export function StepPhotos({ me, onUpdated }) {
  const toast = useToast();
  const input = useRef(null);
  const replacing = useRef(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);

  const startAdd = (replaceId = null) => {
    replacing.current = replaceId;
    input.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const rid = replacing.current;
    setBusy(true);
    try {
      let u = await uploadFile("/profile/photos", file);
      if (rid) {
        const wasMain = me.photos.find((p) => p.id === rid)?.is_main;
        u = await api(`/profile/photos/${rid}`, { method: "DELETE" });
        if (wasMain) {
          const newest = u.photos[u.photos.length - 1];
          if (newest) u = await api(`/profile/photos/${newest.id}/main`, { method: "POST" });
        }
      }
      onUpdated(u);
      toast(rid ? "Photo replaced" : "Photo added", "success");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const act = async (path, method, msg) => {
    setSelected(null);
    setBusy(true);
    try {
      onUpdated(await api(path, { method }));
      toast(msg, "success");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const main = me.photos.find((p) => p.is_main);
  const others = me.photos.filter((p) => !p.is_main);
  const slot = "relative overflow-hidden rounded-[20px] border-2 border-dashed border-[#D48D93] bg-[#F6E3E4] transition-transform hover:scale-[1.01] focus-visible:ring-2";

  return (
    <div data-testid="wizard-step-photos">
      <h1 className="text-[34px] leading-tight">Add your photos</h1>
      <p className="mb-6 mt-1 text-sm text-muted-foreground">At least one photo is required to appear in matches. Your main photo leads your card.</p>
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="photo-file-input" onChange={onFile} />
      <div className="grid max-w-xl grid-cols-3 gap-3">
        <button type="button" data-testid="photo-main-slot" onClick={() => (main ? setSelected(main.id) : startAdd())} className={`${slot} col-span-2 row-span-2`}>
          {main ? (
            <>
              <img src={photoUri(main.url)} alt="Main" className="absolute inset-0 h-full w-full object-cover" />
              <span className="absolute bottom-3 left-3 rounded-full bg-primary px-3 py-1 text-xs font-bold text-white">Main photo</span>
            </>
          ) : (
            <span className="flex h-full flex-col items-center justify-center gap-2 text-primary"><Camera className="h-8 w-8" /><span className="text-sm font-bold">Add main photo</span></span>
          )}
        </button>
        {[0, 1, 2, 3, 4].map((i) => {
          const p = others[i];
          return (
            <button key={i} type="button" data-testid={`photo-slot-${i}`} aria-label={p ? "Photo options" : "Add photo"} onClick={() => (p ? setSelected(p.id) : startAdd())} className={`${slot} flex aspect-[3/4] items-center justify-center`}>
              {p ? <img src={photoUri(p.url)} alt="" className="absolute inset-0 h-full w-full object-cover" /> : <Plus className="h-6 w-6 text-primary" />}
            </button>
          );
        })}
      </div>
      {busy ? <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground" data-testid="photo-uploading"><Loader2 className="h-4 w-4 animate-spin text-primary" />Uploading…</div> : null}
      {main ? (
        <div className="mt-8">
          <p className="mb-3 text-sm font-bold">Preview of your profile card</p>
          <div className="w-60"><MatchCard profile={me.public_preview} preview /></div>
        </div>
      ) : null}
      <Modal open={!!selected} onClose={() => setSelected(null)} title="Photo options" testId="photo-options-sheet">
        <div className="flex flex-col gap-3">
          {selected && !me.photos.find((p) => p.id === selected)?.is_main ? (
            <Button testId="photo-set-main-button" title="Set as main photo" icon={Star} onClick={() => act(`/profile/photos/${selected}/main`, "POST", "Main photo updated")} />
          ) : null}
          <Button testId="photo-replace-button" title="Replace photo" variant="outline" icon={ArrowLeftRight} onClick={() => { const id = selected; setSelected(null); startAdd(id); }} />
          <Button testId="photo-remove-button" title="Remove photo" variant="danger" icon={Trash2} onClick={() => act(`/profile/photos/${selected}`, "DELETE", "Photo removed")} />
        </div>
      </Modal>
    </div>
  );
}
